import appFetch from '../utils/appFetch';

// Types for Bullet Service
export interface BulletBooking {
  id?: number;
  bookingNumber: string;
  trackingNumber: string;
  tmsTrackingNumber?: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  pickupCountry: string;
  pickupCity: string;
  pickupAddress: string;
  pickupPostalCode?: string;
  pickupContactName?: string;
  pickupContactPhone?: string;
  dropoffCountry: string;
  dropoffCity: string;
  dropoffAddress: string;
  dropoffPostalCode?: string;
  dropoffContactName?: string;
  dropoffContactPhone?: string;
  packageType: 'PACKAGE' | 'DOCUMENT';
  weight: number;
  weightUnit: 'KG' | 'LB';
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: 'CM' | 'INCH';
  packageDescription: string;
  pickupDate: string;
  timeSlot: string;
  deliveryDate?: string;
  deliveryTimeSlot?: string;
  serviceType: string;
  serviceFee?: number;
  serviceFeeCurrency?: string;
  totalCost?: number;
  totalCostCurrency?: string;
  status?: string;
  paymentStatus?: string;
  paymentReference?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  confirmedAt?: string;
  pickedUpAt?: string;
  deliveredAt?: string;
}

export interface BulletBookingStats {
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  pickedUpBookings: number;
  inTransitBookings: number;
  deliveredBookings: number;
}

// Bullet Service
export const bulletService = {
  // Booking API
  async createBooking(bookingData: Partial<BulletBooking>): Promise<BulletBooking | null> {
    try {
      const response = await appFetch('/api/bullet/bookings', { 
        method: 'POST', 
        body: JSON.stringify(bookingData) 
      });
      return response.booking || null;
    } catch (error) {
      console.error('Error creating booking:', error);
      return null;
    }
  },

  async getBookingByNumber(bookingNumber: string): Promise<BulletBooking | null> {
    try {
      const response = await appFetch(`/api/bullet/bookings/booking/${bookingNumber}`, { method: 'GET' });
      return response.booking || null;
    } catch (error) {
      console.error('Error fetching booking:', error);
      return null;
    }
  },

  async getBookingByTrackingNumber(trackingNumber: string): Promise<BulletBooking | null> {
    try {
      const response = await appFetch(`/api/bullet/bookings/tracking/${trackingNumber}`, { method: 'GET' });
      return response.booking || null;
    } catch (error) {
      console.error('Error fetching booking by tracking number:', error);
      return null;
    }
  },

  async getAllBookings(): Promise<BulletBooking[]> {
    try {
      const response = await appFetch('/api/bullet/bookings', { method: 'GET' });
      return response?.bookings || [];
    } catch (error) {
      console.error('Error fetching bookings:', error);
      return [];
    }
  },

  async updateBookingStatus(id: number, status: string): Promise<BulletBooking | null> {
    try {
      const response = await appFetch(`/api/bullet/bookings/${id}/status`, { 
        method: 'PUT', 
        body: JSON.stringify({ status }) 
      });
      return response.booking || null;
    } catch (error) {
      console.error('Error updating booking status:', error);
      return null;
    }
  },

  async updateTrackingNumber(bookingNumber: string, trackingNumber: string): Promise<BulletBooking | null> {
    try {
      const response = await appFetch(`/api/bullet/bookings/booking/${bookingNumber}/tracking`, { 
        method: 'PUT', 
        body: JSON.stringify({ trackingNumber }) 
      });
      return response.booking || null;
    } catch (error) {
      console.error('Error updating tracking number:', error);
      return null;
    }
  },

  async getBookingsByCustomerEmail(email: string): Promise<BulletBooking[]> {
    try {
      const response = await appFetch(`/api/bullet/bookings/customer/email/${email}`, { method: 'GET' });
      return response?.bookings || [];
    } catch (error) {
      console.error('Error fetching bookings by customer email:', error);
      return [];
    }
  },

  async getBookingsByCustomerPhone(phone: string): Promise<BulletBooking[]> {
    try {
      const response = await appFetch(`/api/bullet/bookings/customer/phone/${phone}`, { method: 'GET' });
      return response?.bookings || [];
    } catch (error) {
      console.error('Error fetching bookings by customer phone:', error);
      return [];
    }
  },

  async searchBookings(params: {
    customerName?: string;
    customerEmail?: string;
    trackingNumber?: string;
    status?: string;
    pickupCountry?: string;
    dropoffCountry?: string;
  }): Promise<BulletBooking[]> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await appFetch(`/api/bullet/bookings/search?${queryParams.toString()}`, { method: 'GET' });
      return response?.bookings || [];
    } catch (error) {
      console.error('Error searching bookings:', error);
      return [];
    }
  },

  async getBookingStatistics(): Promise<BulletBookingStats | null> {
    try {
      const response = await appFetch('/api/bullet/bookings/statistics', { method: 'GET' });
      return response?.statistics || null;
    } catch (error) {
      console.error('Error fetching booking statistics:', error);
      return null;
    }
  },

  async getSupportedCountries(): Promise<Array<{code: string, name: string, shortName: string, phoneCode: string}>> {
    try {
      const response = await appFetch('/api/bullet/bookings/countries', { method: 'GET' });
      return response?.countries || [];
    } catch (error) {
      console.error('Error fetching countries:', error);
      return [];
    }
  },

  async getTimeSlots(pickupDate: string): Promise<Array<{id: string, time: string, description: string}>> {
    try {
      const response = await appFetch(`/api/bullet/bookings/time-slots?pickupDate=${pickupDate}`, { method: 'GET' });
      return response?.timeSlots || [];
    } catch (error) {
      console.error('Error fetching time slots:', error);
      return [];
    }
  },

  async getDeliveryTimeSlots(pickupSlot: string, pickupDate: string, deliveryDate: string): Promise<Array<{id: string, time: string, description: string}>> {
    try {
      const response = await appFetch(`/api/bullet/bookings/delivery-time-slots?pickupSlot=${pickupSlot}&pickupDate=${pickupDate}&deliveryDate=${deliveryDate}`, { method: 'GET' });
      return response?.timeSlots || [];
    } catch (error) {
      console.error('Error fetching delivery time slots:', error);
      return [];
    }
  },

  async getBookingServiceTypes(): Promise<Array<{id: string, name: string, description: string, estimatedDays: number, priceMultiplier: number, features: string}>> {
    try {
      const response = await appFetch('/api/bullet/bookings/service-types', { method: 'GET' });
      return response?.serviceTypes || [];
    } catch (error) {
      console.error('Error fetching booking service types:', error);
      return [];
    }
  },

  // Utility methods
  async getAvailableCountries(): Promise<string[]> {
    try {
      // Return a comprehensive list of countries supported by Bullet service
      return [
        'US', 'CA', 'MX', 'GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'CH', 'SE', 'NO', 'DK', 'FI',
        'PL', 'CZ', 'HU', 'SK', 'SI', 'HR', 'RO', 'BG', 'GR', 'CY', 'MT', 'IE', 'PT', 'LU', 'EE', 'LV', 'LT',
        'JP', 'KR', 'CN', 'HK', 'SG', 'MY', 'TH', 'VN', 'PH', 'ID', 'IN', 'BD', 'PK', 'LK', 'MM', 'KH', 'LA',
        'AU', 'NZ', 'FJ', 'PG', 'NC', 'VU', 'SB', 'TO', 'WS', 'KI', 'TV', 'NR', 'PW', 'FM', 'MH',
        'BR', 'AR', 'CL', 'CO', 'PE', 'VE', 'UY', 'PY', 'BO', 'EC', 'GY', 'SR', 'GF', 'FK',
        'ZA', 'NG', 'KE', 'GH', 'EG', 'MA', 'TN', 'DZ', 'LY', 'SD', 'ET', 'UG', 'TZ', 'RW', 'BI', 'DJ', 'SO',
        'ER', 'SS', 'CF', 'TD', 'NE', 'ML', 'BF', 'CI', 'LR', 'SL', 'GN', 'GW', 'GM', 'SN', 'MR', 'CV', 'ST',
        'AO', 'ZM', 'ZW', 'BW', 'NA', 'SZ', 'LS', 'MG', 'MU', 'SC', 'KM', 'YT', 'RE', 'MZ', 'MW', 'MG'
      ];
    } catch (error) {
      return ['US', 'CA', 'GB', 'DE', 'FR', 'AU', 'JP', 'SG', 'IN', 'AE'];
    }
  },

  async getAvailableCurrencies(): Promise<string[]> {
    try {
      return [
        'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'HKD', 'SGD', 'NZD', 'SEK', 'NOK', 'DKK',
        'PLN', 'CZK', 'HUF', 'RUB', 'BRL', 'MXN', 'ARS', 'CLP', 'COP', 'PEN', 'VES', 'UYU', 'PYG', 'BOB',
        'INR', 'PKR', 'BDT', 'LKR', 'NPR', 'AFN', 'KZT', 'UZS', 'KGS', 'TJS', 'TMT', 'AZN', 'AMD', 'GEL',
        'TRY', 'ILS', 'JOD', 'LBP', 'SYP', 'IQD', 'IRR', 'KWD', 'BHD', 'QAR', 'AED', 'OMR', 'YER', 'SAR',
        'EGP', 'LYD', 'TND', 'DZD', 'MAD', 'MUR', 'SCR', 'KES', 'UGX', 'TZS', 'RWF', 'BIF', 'ETB', 'DJF',
        'SOS', 'ERN', 'SSP', 'XAF', 'XOF', 'ZAR', 'BWP', 'SZL', 'LSL', 'MZN', 'MWK', 'ZMW', 'ZWL', 'AOA',
        'MGA', 'MUR', 'SCR', 'KMF', 'MAD', 'EGP', 'NGN', 'GHS', 'XOF', 'XAF'
      ];
    } catch (error) {
      return ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'HKD', 'SGD'];
    }
  },

  async getServiceTypes(): Promise<Array<{id: string, name: string, description: string, estimatedDays: number, priceMultiplier: number}>> {
    try {
      return [
        { id: 'EXPRESS', name: 'Express Delivery', description: 'Fastest delivery option', estimatedDays: 1, priceMultiplier: 2.5 },
        { id: 'STANDARD', name: 'Standard Delivery', description: 'Regular delivery service', estimatedDays: 3, priceMultiplier: 1.0 },
        { id: 'ECONOMY', name: 'Economy Delivery', description: 'Cost-effective option', estimatedDays: 7, priceMultiplier: 0.7 },
        { id: 'FREIGHT', name: 'Freight Service', description: 'For large shipments', estimatedDays: 5, priceMultiplier: 1.8 }
      ];
    } catch (error) {
      return [];
    }
  }
};

export default bulletService;